/**
 * יישור נוסעים לפני HotelBookRequest מול סדר allocatePassengersToRooms בשרת:
 * קודם כל המבוגרים לפי סדר package.Rooms, אחר כך כל הילדים באותו סדר.
 */

/** @param {unknown} rooms */
export function normalizeSupplierRoomsArray(rooms) {
  if (!rooms) return [];
  if (Array.isArray(rooms)) return rooms;
  if (typeof rooms === "object") return Object.values(rooms);
  return [];
}

/** @param {unknown} passengerInfo */
export function normalizePassengerRoomArrays(passengerInfo) {
  if (passengerInfo == null) return [];
  if (Array.isArray(passengerInfo)) return passengerInfo;
  if (typeof passengerInfo === "object") {
    return Object.keys(passengerInfo)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => passengerInfo[k]);
  }
  return [];
}

/**
 * @param {object} room — roomsAndPaxInfo element
 * @returns {object[]}
 */
export function flattenDynamicPackageRoomPassengers(room) {
  if (!room || typeof room !== "object") return [];
  const adults = Array.isArray(room.adults) ? room.adults : [];
  const kids = Array.isArray(room.kids) ? room.kids : [];
  const infants = Array.isArray(room.infants) ? room.infants : [];
  return [
    ...adults.map((a) => ({
      ...a,
      isKid: false,
      isInfant: false,
    })),
    ...kids.map((k) => ({
      ...k,
      isKid: true,
      isInfant: false,
    })),
    ...infants.map((inf) => ({
      ...inf,
      isKid: false,
      isInfant: true,
    })),
  ];
}

function isAdultSlot(p) {
  if (!p || typeof p !== "object") return false;
  return !p.isKid && !p.isInfant;
}

function isChildSlot(p) {
  if (!p || typeof p !== "object") return false;
  return Boolean(p.isKid || p.isInfant);
}

/** @param {object} p */
function hintPackageRoomIndex(p, packageRooms) {
  const idxKeys = [
    "supplierRoomIndex",
    "passengerRoomIndex",
    "roomIndex",
    "roomNumber",
  ];
  for (const k of idxKeys) {
    if (p[k] == null || p[k] === "") continue;
    const n = Number(p[k]);
    if (Number.isFinite(n) && n >= 0 && n < packageRooms.length) {
      return n;
    }
  }
  const rid =
    p.roomId ??
    p.Allocation ??
    p.intendedRoomId ??
    p.selectedRoomId ??
    p.roomUUID ??
    null;
  if (rid == null || rid === "") return null;
  const rs = String(rid);
  const j = packageRooms.findIndex(
    (room) =>
      String(room?.Id ?? room?.id ?? room?.RoomId ?? "") === rs
  );
  return j >= 0 ? j : null;
}

function summarizePassengersForTrace(labelList) {
  return labelList.map((entry, index) => ({
    index,
    name:
      `${entry.raw?.firstname ?? ""} ${entry.raw?.lastname ?? ""}`.trim() ||
      "(ריק)",
    isKid: Boolean(entry.raw?.isKid),
    isInfant: Boolean(entry.raw?.isInfant),
    hints: {
      roomIndex:
        entry.raw?.passengerRoomIndex ??
        entry.raw?.roomIndex ??
        entry.raw?.roomNumber ??
        null,
      roomId:
        entry.raw?.roomId ??
        entry.raw?.Allocation ??
        entry.raw?.intendedRoomId ??
        entry.raw?.selectedRoomId ??
        null,
    },
  }));
}

function buildPassengerApiShape(passenger, { isChild, kidAge }) {
  const prefix =
    passenger.title ||
    passenger.gender ||
    (isChild ? "Mr" : passenger.gender || "Mr");
  const rec = {
    PersonDetails: {
      Name: {
        GivenName: passenger.firstname || "",
        NamePrefix: prefix || "Mr",
        Surname: passenger.lastname || "",
      },
      Type: isChild ? 1 : 0,
      ...(isChild && kidAge != null ? { Age: kidAge } : {}),
    },
  };
  if (passenger.email) {
    rec.Email = { Value: passenger.email };
  }
  if (passenger.cell) {
    rec.Telephone = { PhoneNumber: passenger.cell };
  }
  return rec;
}

/**
 * @param {{
 *   packageRooms: unknown,
 *   passengerRoomsInput: unknown,
 *   inputKind: 'nested-room-arrays' | 'dynamic-pax-structured',
 * }} opts
 */
export function buildPassengersForGemonixBooking(opts) {
  const packageRooms = normalizeSupplierRoomsArray(opts.packageRooms);
  let passengerRoomsRaw =
    opts.inputKind === "dynamic-pax-structured"
      ? opts.passengerRoomsInput
      : normalizePassengerRoomArrays(opts.passengerRoomsInput);

  /** @type {object[][]} */
  let buckets = [];

  let blockedDueToMissingRoomMapping = false;
  let mappingMode = "unset";

  if (opts.inputKind === "dynamic-pax-structured") {
    const arr = Array.isArray(passengerRoomsRaw) ? passengerRoomsRaw : [];
    buckets = arr.map((room) => flattenDynamicPackageRoomPassengers(room));
    if (buckets.length !== packageRooms.length) {
      blockedDueToMissingRoomMapping = true;
      mappingMode = "dynamic-length-mismatch";
    } else {
      mappingMode = "dynamic-index";
    }
  } else {
    const pr = Array.isArray(passengerRoomsRaw)
      ? passengerRoomsRaw
      : normalizePassengerRoomArrays(passengerRoomsRaw);

    if (packageRooms.length === pr.length && packageRooms.length >= 1) {
      buckets = pr.map((room) =>
        Array.isArray(room) ? room : room ? [room] : []
      );
      mappingMode = "strict-index";
    } else if (packageRooms.length === 1 && pr.length === 1) {
      buckets = [Array.isArray(pr[0]) ? pr[0] : pr[0] ? [pr[0]] : []];
      mappingMode = "single-room";
    } else {
      const hintedBuckets = packageRooms.map(() => []);
      let someHinted = false;
      let allHinted = true;
      const flatPassengers = [];
      pr.forEach((room, ri) => {
        const row = Array.isArray(room) ? room : room ? [room] : [];
        row.forEach((raw) => {
          flatPassengers.push({ raw, sourceRoomIdx: ri });
        });
      });

      for (const { raw } of flatPassengers) {
        const hi = hintPackageRoomIndex(raw, packageRooms);
        if (hi != null) {
          someHinted = true;
          hintedBuckets[hi].push(raw);
        } else {
          allHinted = false;
        }
      }

      if (flatPassengers.length === 0) {
        buckets = packageRooms.map(() => []);
        mappingMode = "empty-passengers";
      } else if (someHinted && allHinted) {
        buckets = hintedBuckets;
        mappingMode = "hint-all";
      } else if (someHinted && !allHinted) {
        blockedDueToMissingRoomMapping = true;
        mappingMode = "hint-partial-unsafe";
      } else {
        blockedDueToMissingRoomMapping = true;
        mappingMode = "no-safe-mapping";
      }
    }
  }

  const packageRoomOrder = packageRooms.map((room, idx) => ({
    index: idx,
    Id: room?.Id ?? room?.id ?? room?.RoomId ?? null,
    AdultsCount: Number(room?.AdultsCount ?? 0),
    KidsAges: Array.isArray(room?.KidsAges) ? [...room.KidsAges] : [],
  }));

  const passengersBefore = [];
  buckets.forEach((bucket, ri) => {
    bucket.forEach((raw) => {
      passengersBefore.push({ raw, sourceBucket: ri });
    });
  });
  const bucketPassengerTotal = passengersBefore.length;

  if (blockedDueToMissingRoomMapping || buckets.length !== packageRooms.length) {
    const trace = {
      packageRoomOrder,
      passengersBefore: summarizePassengersForTrace(passengersBefore),
      passengersForBooking: [],
      countsByRoom: [],
      hasPassengerRoomDrift: true,
      blockedDueToMissingRoomMapping: true,
      mappingMode,
      bucketPassengerTotal,
    };
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        "[HOTEL_BOOK_PASSENGER_ORDER_TRACE]",
        trace,
        "\nמיפוי חדרים לא אמין או אורך לא תואם ל-package.Rooms — לא נשלח לספק."
      );
    }
    return {
      ok: false,
      passengersForBooking: [],
      errorMessage:
        "מבנה הנוסעים לא תואם למספר החדרים בחבילה, או שחסר שיוך חדר אמין. נא לעדכן פרטי נוסעים או לבחור חבילה מחדש.",
      trace,
    };
  }

  /** @type {{ roomIndex: number, roomId: string|null, adultsRequired: number, childrenRequired: number, adultsAvailable: number, childrenAvailable: number, drift: boolean }[]} */
  const countsByRoom = [];
  let hasPassengerRoomDrift = mappingMode !== "strict-index" && mappingMode !== "single-room" && mappingMode !== "dynamic-index";

  const adultsByRoom = buckets.map((bucket) =>
    bucket.filter((p) => isAdultSlot(p))
  );
  const childrenByRoom = buckets.map((bucket) =>
    bucket.filter((p) => isChildSlot(p))
  );

  for (let i = 0; i < packageRooms.length; i++) {
    const room = packageRooms[i];
    const adultsRequired = Number(room?.AdultsCount ?? 0);
    const kidsAges = Array.isArray(room?.KidsAges) ? room.KidsAges : [];
    const childrenRequired = kidsAges.length;
    const adultsAvailable = adultsByRoom[i].length;
    const childrenAvailable = childrenByRoom[i].length;
    const drift =
      adultsAvailable !== adultsRequired || childrenAvailable !== childrenRequired;
    if (drift) hasPassengerRoomDrift = true;
    countsByRoom.push({
      roomIndex: i,
      roomId: room?.Id ?? room?.id ?? null,
      adultsRequired,
      childrenRequired,
      adultsAvailable,
      childrenAvailable,
      drift,
    });
  }

  const capacityError = countsByRoom.some((c) => c.drift);
  if (capacityError) {
    const trace = {
      packageRoomOrder,
      passengersBefore: summarizePassengersForTrace(passengersBefore),
      passengersForBooking: [],
      countsByRoom,
      hasPassengerRoomDrift: true,
      blockedDueToMissingRoomMapping: false,
      mappingMode,
      bucketPassengerTotal,
    };
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[HOTEL_BOOK_PASSENGER_ORDER_TRACE]", trace);
    }
    return {
      ok: false,
      passengersForBooking: [],
      errorMessage:
        "מספר נוסעים לחדר לא תואם להגדרת החבילה (מבוגרים / ילדים). נא להשלים את הפרטים לפי החדרים.",
      trace,
    };
  }

  /** @type {object[]} */
  const passengersForBooking = [];
  /** @type {object[]} */
  const passengersForBookingTrace = [];

  for (let i = 0; i < packageRooms.length; i++) {
    const room = packageRooms[i];
    const expectedRoomId = room?.Id ?? room?.id ?? room?.RoomId ?? null;
    const adultsNeeded = Number(room?.AdultsCount ?? 0);
    const takeAdults = adultsByRoom[i].slice(0, adultsNeeded);
    for (const p of takeAdults) {
      const row = buildPassengerApiShape(p, { isChild: false });
      passengersForBooking.push(row);
      passengersForBookingTrace.push({
        index: passengersForBookingTrace.length,
        name:
          `${row.PersonDetails?.Name?.GivenName ?? ""} ${row.PersonDetails?.Name?.Surname ?? ""}`.trim(),
        expectedRoomId,
        adultOrChild: "adult",
        age: row.PersonDetails?.Age ?? null,
      });
    }
  }

  for (let i = 0; i < packageRooms.length; i++) {
    const room = packageRooms[i];
    const expectedRoomId = room?.Id ?? room?.id ?? room?.RoomId ?? null;
    const kidsAges = Array.isArray(room?.KidsAges) ? room.KidsAges : [];
    const takeKids = childrenByRoom[i].slice(0, kidsAges.length);
    for (let j = 0; j < takeKids.length; j++) {
      const p = takeKids[j];
      const kidAge = kidsAges[j];
      const row = buildPassengerApiShape(p, { isChild: true, kidAge });
      passengersForBooking.push(row);
      passengersForBookingTrace.push({
        index: passengersForBookingTrace.length,
        name:
          `${row.PersonDetails?.Name?.GivenName ?? ""} ${row.PersonDetails?.Name?.Surname ?? ""}`.trim(),
        expectedRoomId,
        adultOrChild: "child",
        age: row.PersonDetails?.Age ?? kidAge ?? null,
      });
    }
  }

  const trace = {
    packageRoomOrder,
    passengersBefore: summarizePassengersForTrace(passengersBefore),
    passengersForBooking: passengersForBookingTrace,
    countsByRoom,
    hasPassengerRoomDrift,
    blockedDueToMissingRoomMapping: false,
    mappingMode,
    leadPaxRoomIdExpected: passengersForBookingTrace[0]?.expectedRoomId ?? null,
    passengerTypes: passengersForBookingTrace.map((p) => p.adultOrChild),
    bucketPassengerTotal,
  };

  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.error("[HOTEL_BOOK_PASSENGER_ORDER_TRACE]", trace);
  }

  return {
    ok: true,
    passengersForBooking,
    errorMessage: "",
    trace,
  };
}
